import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { useUserStore } from '@/stores/modules/user'

/**
 * 初始化 Sentry 错误监控
 * @param app Vue 应用实例
 * @param router Vue Router 实例
 */
export function initSentry(app: App, router: Router) {
  // 只在生产环境初始化 Sentry
  if (import.meta.env.MODE !== 'production') {
    return
  }

  Sentry.init({
    app,
    // Sentry 项目的 DSN (Data Source Name)，用于标识错误发送到哪个项目
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // 环境标识，用于区分不同的部署环境
    environment: import.meta.env.MODE,

    // 是否发送默认的个人身份信息 (PII) 到 Sentry
    // 例如：自动收集事件中的 IP 地址
    sendDefaultPii: true,

    // 集成配置
    integrations: [
      // 浏览器追踪集成，用于性能监控
      Sentry.browserTracingIntegration({
        router
      })
    ],

    // 性能追踪采样率 (0.0 到 1.0)
    // 1.0 表示捕获 100% 的事务用于性能监控
    tracesSampleRate: 0.1,

    // 错误采样率 (0.0 到 1.0)
    // 1.0 表示捕获 100% 的错误
    sampleRate: 1.0,

    // 控制分布式追踪的目标 URL
    // 用于确定哪些 URL 应该启用分布式追踪
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

    // 应用版本号，用于错误追踪和发布管理
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // 在发送到 Sentry 之前修改事件数据
    beforeSend(event, hint) {
      // 可以在这里过滤或修改错误事件
      // console.log('Sentry event:', event, hint)

      // 获取错误信息
      const errorMsg = (hint.originalException as any)?.message || ''
      // 获取错误关联的URL（如脚本/CSS路径）
      // 过滤条件：匹配错误信息或URL
      const isIgnored =
        // 过滤“动态导入模块”错误
        errorMsg.includes('Failed to fetch dynamically imported module') ||
        // 过滤“CSS预加载”错误
        errorMsg.includes('Unable to preload CSS')

      if (isIgnored) {
        return null
      } // 返回null则不上报

      return event
    }

    // 在发送到 Sentry 之前修改事务数据
    // beforeSendTransaction(event) {
    //   // 可以在这里过滤或修改性能事务
    //   return event
    // }

    // 以下是其他可选配置项，根据需要启用

    // 最大面包屑数量，用于错误上下文追踪
    // maxBreadcrumbs: 100,

    // 是否启用自动会话追踪
    // autoSessionTracking: true,

    // 会话采样率
    // sessionSampleRate: 1.0,

    // 是否启用用户反馈对话框
    // showReportDialog: false,

    // 初始作用域配置
    // initialScope: {
    //   tags: { component: 'ui' },
    //   user: { id: '123', email: 'user@example.com' },
    //   level: 'info'
    // },

    // 忽略的错误类型
    // ignoreErrors: [
    //   'Non-Error promise rejection captured',
    //   'ResizeObserver loop limit exceeded'
    // ],

    // 忽略的 URL 模式
    // denyUrls: [
    //   /extensions\//i,
    //   /^chrome:\/\//i
    // ],

    // 允许的 URL 模式
    // allowUrls: [
    //   /https:\/\/yoursite\.com/
    // ],

    // 是否启用调试模式
    // debug: false,

    // 传输选项
    // transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    // 堆栈追踪配置
    // stackParser: Sentry.defaultStackParser,

    // 是否启用追踪
    // enableTracing: true,

    // 性能监控的空闲超时时间（毫秒）
    // idleTimeout: 1000,

    // 最终超时时间（毫秒）
    // finalTimeout: 30000,

    // 心跳间隔（毫秒）
    // heartbeatInterval: 5000,

    // 是否启用长任务监控
    // enableLongTask: true,

    // 是否启用 INP (Interaction to Next Paint) 监控
    // enableInp: true
  })
}

/**
 * 手动捕获错误
 * @param error 错误对象
 * @param context 错误上下文
 */
export function captureError(error: Error, context?: Record<string, any>) {
  if (import.meta.env.MODE === 'production') {
    Sentry.withScope(scope => {
      if (context) {
        scope.setContext('error_context', context)
      }
      Sentry.captureException(error)
    })
  } else {
    console.error('Error captured:', error, context)
  }
}

/**
 * 手动发送消息
 * @param message 消息内容
 * @param level 消息级别
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (import.meta.env.MODE === 'production') {
    Sentry.captureMessage(message, level)
  } else {
    // console.log(`Sentry message [${level}]:`, message)
  }
}

/**
 * 设置用户信息
 * @param user 用户信息
 */
export function setUser(user: Sentry.User) {
  if (import.meta.env.MODE === 'production') {
    Sentry.setUser(user)
  }
}

/**
 * 设置标签
 * @param tags 标签对象
 */
export function setTags(tags: Record<string, string>) {
  if (import.meta.env.MODE === 'production') {
    Sentry.setTags(tags)
  }
}

/**
 * 添加面包屑
 * @param breadcrumb 面包屑信息
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (import.meta.env.MODE === 'production') {
    Sentry.addBreadcrumb(breadcrumb)
  }
}

export const setSentryUserInfo = async () => {
  const userStore = useUserStore()
  const userInfo = await userStore.getUserInfo()

  setTags({
    id: userInfo?.id || '',
    idNum: userInfo?.idNum + '' || '',
    ...(userInfo?.areaInfo || {})
  })
}
